import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, Shield, Eye, UserCheck, X, Save } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = {
  super_admin: { label: { pt: 'Super Admin', en: 'Super Admin' }, color: 'bg-red-100 text-red-700', icon: Shield },
  manager: { label: { pt: 'Gestor', en: 'Manager' }, color: 'bg-blue-100 text-blue-700', icon: UserCheck },
  viewer: { label: { pt: 'Visualizador', en: 'Viewer' }, color: 'bg-gray-100 text-gray-700', icon: Eye }
};

const AdminUserManagement = ({ language }) => {
  const { token, isSuperAdmin, user } = useAdmin();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'viewer'
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', role: 'viewer' });
    setError('');
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', role: 'viewer' });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setSubmitting(true);

    console.log('=== USER CREATION DEBUG ===');
    console.log('Form data:', formData);
    console.log('Token:', token ? 'Present' : 'Missing');
    console.log('Editing user:', editingUser);

    try {
      if (editingUser) {
        // Update user
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        console.log('Updating user:', updateData);
        const response = await axios.patch(
          `${API}/admin/users/${editingUser.id}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Update response:', response.data);
      } else {
        // Create user
        if (!formData.password) {
          setError(language === 'pt' ? 'A senha é obrigatória' : 'Password is required');
          setSubmitting(false);
          return;
        }
        
        console.log('Creating user with data:', formData);
        const response = await axios.post(
          `${API}/admin/users`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Create response:', response.data);
      }
      
      closeModal();
      fetchUsers();
    } catch (error) {
      console.error('=== USER CREATION ERROR ===');
      console.error('Error:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      const errorMessage = error.response?.data?.detail || (language === 'pt' ? 'Erro ao guardar utilizador' : 'Error saving user');
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/admin/users/${userId}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(language === 'pt' 
      ? `Tem certeza que deseja excluir o utilizador "${userName}"?` 
      : `Are you sure you want to delete user "${userName}"?`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || (language === 'pt' ? 'Erro ao excluir utilizador' : 'Error deleting user'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString(language === 'pt' ? 'pt-PT' : 'en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isSuperAdmin()) {
    return (
      <div className="text-center py-12">
        <Shield size={48} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">
          {language === 'pt' 
            ? 'Apenas Super Admins podem gerir utilizadores' 
            : 'Only Super Admins can manage users'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-heading font-semibold">
          {language === 'pt' ? 'Gestão de Utilizadores' : 'User Management'}
        </h2>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-light transition-colors flex items-center gap-2"
          data-testid="create-user-btn"
        >
          <Plus size={18} />
          {language === 'pt' ? 'Novo Utilizador' : 'New User'}
        </button>
      </div>

      {loading ? (
        <p>{language === 'pt' ? 'Carregando...' : 'Loading...'}</p>
      ) : users.length === 0 ? (
        <p className="text-gray-600">{language === 'pt' ? 'Nenhum utilizador encontrado' : 'No users found'}</p>
      ) : (
        <div className="space-y-3">
          {users.map((u) => {
            const roleConfig = ROLES[u.role] || ROLES.viewer;
            const RoleIcon = roleConfig.icon;
            const isCurrentUser = u.email === user?.email;
            
            return (
              <div 
                key={u.id} 
                className={`border rounded-xl p-4 bg-white shadow-sm ${!u.is_active ? 'opacity-60' : ''} ${isCurrentUser ? 'border-primary' : 'border-gray-200'}`}
                data-testid={`user-${u.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${roleConfig.color}`}>
                      <RoleIcon size={24} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-semibold text-lg">{u.name}</h3>
                        {isCurrentUser && (
                          <span className="text-xs px-2 py-0.5 bg-primary text-white rounded-full">
                            {language === 'pt' ? 'Você' : 'You'}
                          </span>
                        )}
                        {!u.is_active && (
                          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                            {language === 'pt' ? 'Inativo' : 'Inactive'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{u.email}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${roleConfig.color}`}>
                          {roleConfig.label[language]}
                        </span>
                        <span className="text-xs text-gray-400">
                          {language === 'pt' ? 'Último login:' : 'Last login:'} {formatDate(u.last_login)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {!isCurrentUser && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleUserStatus(u.id, u.is_active)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          u.is_active 
                            ? 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100' 
                            : 'bg-green-50 text-green-600 hover:bg-green-100'
                        }`}
                        data-testid={`toggle-user-${u.id}`}
                      >
                        {u.is_active 
                          ? (language === 'pt' ? 'Desativar' : 'Deactivate')
                          : (language === 'pt' ? 'Ativar' : 'Activate')
                        }
                      </button>
                      <button
                        onClick={() => openEditModal(u)}
                        className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors flex items-center gap-1"
                        data-testid={`edit-user-${u.id}`}
                      >
                        <Edit size={14} />
                        {language === 'pt' ? 'Editar' : 'Edit'}
                      </button>
                      <button
                        onClick={() => deleteUser(u.id, u.name)}
                        className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center gap-1"
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 size={14} />
                        {language === 'pt' ? 'Excluir' : 'Delete'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-heading font-semibold">
                {editingUser 
                  ? (language === 'pt' ? 'Editar Utilizador' : 'Edit User')
                  : (language === 'pt' ? 'Novo Utilizador' : 'New User')
                }
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Nome' : 'Name'}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="user-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="user-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Senha' : 'Password'} 
                  {editingUser && <span className="text-gray-400 text-xs ml-1">({language === 'pt' ? 'deixe vazio para manter' : 'leave empty to keep'})</span>}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required={!editingUser}
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="user-password-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Função' : 'Role'}
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="user-role-select"
                >
                  <option value="super_admin">{language === 'pt' ? 'Super Admin' : 'Super Admin'}</option>
                  <option value="manager">{language === 'pt' ? 'Gestor' : 'Manager'}</option>
                  <option value="viewer">{language === 'pt' ? 'Visualizador' : 'Viewer'}</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.role === 'super_admin' && (language === 'pt' ? 'Acesso total ao sistema' : 'Full system access')}
                  {formData.role === 'manager' && (language === 'pt' ? 'Pode ver e editar pedidos' : 'Can view and edit requests')}
                  {formData.role === 'viewer' && (language === 'pt' ? 'Apenas pode visualizar' : 'View only access')}
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  {language === 'pt' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-light transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="save-user-btn"
                >
                  <Save size={16} />
                  {submitting 
                    ? (language === 'pt' ? 'A guardar...' : 'Saving...') 
                    : (language === 'pt' ? 'Guardar' : 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;
